import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword, confirmResetPassword, getCurrentUser } from 'aws-amplify/auth';
import Header from './home/Header';
import AuthHero from './auth/AuthHero';

function ResetPassword() {
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword({ username: email });
      setStep('confirm');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      setStep('success');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex flex-1">
          <AuthHero />
          <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
            <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa fa-check text-green-600 text-2xl"></i>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Password Reset!</h2>
              <p className="text-gray-600 mb-6 text-sm">Your password has been successfully reset.</p>
              <Link to="/login" className="btn-primary inline-block text-sm py-3 px-6">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex flex-1">
          <AuthHero />
          <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
            <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Enter Reset Code</h2>
              <p className="text-gray-600 mb-6 text-sm">We sent a code to <strong>{email}</strong></p>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
                  {error}
                </div>
              )}

              <form onSubmit={handleConfirmReset} className="flex flex-col gap-3">
                <input
                  type="text"
                  placeholder="Reset code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  className="p-3 border border-gray-300 rounded-lg text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="8"
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn-primary text-sm py-3 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-1">
        <AuthHero />
        <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
          <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Reset Password</h2>
            <p className="text-gray-600 mb-6 text-sm">Enter your email to receive a reset code</p>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
                {error}
              </div>
            )}

            <form onSubmit={handleRequestReset} className="flex flex-col gap-3">
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={loading}
                className={`btn-primary text-sm py-3 ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>

            <p className="mt-4 text-xs text-gray-600 text-center">
              Remember your password?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium no-underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
